import React, { useState } from 'react';
import { NearbyStore, User } from '../shared/types';
import { useLanguage } from '../services/LanguageContext';
import { locationApi } from '../services/location';
import StoreSelector from './StoreSelector';

// #26: shown when an optimisation is triggered far (>OUT_OF_RADIUS_KM) from all
// of the user's favourite stores. Offers three choices:
//   A) Find stores near my home — one-off resolution, used for this run only
//   B) Update my favourite stores — persistent change via the store picker
//   C) Use my saved stores — proceed with existing favourites regardless
//
// onResolve is called with the store list the optimiser should use for THIS run
// (the favourites for B/C, the one-off list for A). onCancel aborts the run.

interface Props {
  user: User;
  distanceKm: number | null;
  onResolve: (stores: NearbyStore[]) => void;
  onUpdateUser: (user: User) => void;
  onCancel: () => void;
}

const OutOfRadiusModal: React.FC<Props> = ({ user, distanceKm, onResolve, onUpdateUser, onCancel }) => {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [picking, setPicking] = useState(false);
  const [oneOff, setOneOff] = useState<NearbyStore[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const favourites = (user.nearbyStores || []).filter(s =>
    (user.favouriteStores || []).some(f => f.storeId === s.storeId));

  // Option A — resolve stores near home for this request only.
  const handleFindNearby = async () => {
    setBusy(true); setError('');
    try {
      const { stores } = await locationApi.storesNearHome();
      if (!stores.length) { setError(t('loc.outOfRadius.noneFound')); setBusy(false); return; }
      onResolve(stores);
    } catch (e: any) { setError(e.message); setBusy(false); }
  };

  // Option B — open the picker pre-populated with stores near home.
  const handleStartUpdate = async () => {
    setBusy(true); setError('');
    try {
      const { stores } = await locationApi.storesNearHome();
      setOneOff(stores);
      setSelected(new Set());
      setPicking(true);
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };

  const handleSaveUpdate = async () => {
    setBusy(true); setError('');
    try {
      const updated = await locationApi.saveFavouriteStores(Array.from(selected));
      onUpdateUser(updated);
      const chosen = (updated.nearbyStores || []).filter(s =>
        (updated.favouriteStores || []).some(f => f.storeId === s.storeId));
      onResolve(chosen);
    } catch (e: any) { setError(e.message); setBusy(false); }
  };

  const toggle = (storeId: string) => setSelected(prev => {
    const next = new Set(prev);
    next.has(storeId) ? next.delete(storeId) : next.add(storeId);
    return next;
  });

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4" role="dialog" aria-modal="true" aria-labelledby="oor-title">
      <div className="max-w-lg w-full max-h-[90vh] overflow-y-auto bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-[2rem] p-8 shadow-2xl">
        <h2 id="oor-title" className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
          {t('loc.outOfRadius.title')}
        </h2>
        <p className="mt-2 text-slate-600 dark:text-slate-300 font-medium">
          {distanceKm != null
            ? t('loc.outOfRadius.subtitle').replace('{km}', distanceKm.toFixed(0))
            : t('loc.outOfRadius.subtitleNoFav')}
        </p>

        {error && (
          <div role="alert" className="mt-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-2xl text-sm font-bold">
            <i className="fas fa-circle-exclamation mr-2" aria-hidden="true"></i>{error}
          </div>
        )}

        {!picking ? (
          <div className="mt-6 flex flex-col gap-3">
            <button type="button" onClick={handleFindNearby} disabled={busy}
              className="w-full py-4 bg-emerald-500 text-dark font-black rounded-2xl hover:bg-emerald-400 transition-all flex items-center justify-center gap-3 disabled:opacity-50">
              <i className="fas fa-location-crosshairs" aria-hidden="true"></i>{t('loc.outOfRadius.optionA')}
            </button>
            <button type="button" onClick={handleStartUpdate} disabled={busy}
              className="w-full py-4 bg-white dark:bg-dark-border/30 border-2 border-slate-300 dark:border-dark-border text-slate-700 dark:text-white font-black rounded-2xl hover:border-emerald-500 transition-all disabled:opacity-50">
              <i className="fas fa-star mr-2" aria-hidden="true"></i>{t('loc.outOfRadius.optionB')}
            </button>
            <button type="button" onClick={() => onResolve(favourites)} disabled={busy}
              className="w-full py-4 bg-transparent border-2 border-slate-300 dark:border-dark-border text-slate-600 dark:text-slate-300 font-black rounded-2xl hover:border-slate-400 transition-all disabled:opacity-50">
              {t('loc.outOfRadius.optionC')}
            </button>
            <button type="button" onClick={onCancel} disabled={busy}
              className="w-full py-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 font-bold transition-colors">
              {t('common.cancel')}
            </button>
          </div>
        ) : (
          <div className="mt-6">
            <StoreSelector stores={oneOff || []} selected={selected} onToggle={toggle} />
            <div className="mt-6 flex flex-col gap-3">
              <button type="button" onClick={handleSaveUpdate} disabled={busy}
                className="w-full py-4 bg-emerald-500 text-dark font-black rounded-2xl hover:bg-emerald-400 transition-all flex items-center justify-center gap-3 disabled:opacity-50">
                {busy ? <i className="fas fa-spinner animate-spin" aria-hidden="true"></i> : <i className="fas fa-check" aria-hidden="true"></i>}
                {t('loc.outOfRadius.saveFavourites')}
              </button>
              <button type="button" onClick={() => setPicking(false)} disabled={busy}
                className="w-full py-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 font-bold transition-colors">
                {t('common.back')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutOfRadiusModal;
